import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Renders an investor-email delivery status as a coloured pill.
 *
 * The status string is provider-neutral: it is either the webhook delivery
 * truth (`delivered`, `bounced`, `soft_bounced`, `spam`, `rejected`, `failed`)
 * or, before any webhook arrives, the queue-lifecycle fallback (`sent`,
 * `pending`, `cancelled`). Unknown values fall through to a plain outline pill.
 */
export function DeliveryStatusBadge({
  status,
  className,
}: {
  status: string;
  className?: string;
}) {
  const normalized = status.toLowerCase().trim();

  const tone = (() => {
    if (normalized === 'delivered') {
      return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800';
    }
    if (normalized === 'sent') {
      // Accepted by the relay, not yet confirmed delivered.
      return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800';
    }
    if (['pending', 'sending', 'queued', 'processing'].includes(normalized)) {
      return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800';
    }
    if (
      ['bounced', 'soft_bounced', 'rejected', 'spam', 'failed'].includes(
        normalized
      )
    ) {
      return 'bg-destructive/10 text-destructive border-destructive/30';
    }
    return '';
  })();

  // Title-case the raw status for display ("soft_bounced" -> "Soft Bounced").
  const label = normalized
    .split(/[\s_]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return (
    <Badge variant="outline" className={cn(tone, className)}>
      {label}
    </Badge>
  );
}
